import { Switch } from '@headlessui/react';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

interface ThemeToggleProps {
  variant?: 'button' | 'switch';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function ThemeToggle({ variant = 'switch', size = 'md', className = '' }: ThemeToggleProps) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  const sizeClasses = {
    sm: 'h-6 w-11',
    md: 'h-7 w-12',
    lg: 'h-8 w-14'
  };

  const iconSizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  };

  if (variant === 'button') {
    return (
      <button
        onClick={toggleTheme}
        className={`
          inline-flex items-center justify-center rounded-lg border border-border
          bg-background hover:bg-muted transition-colors duration-200
          p-2 text-foreground hover:text-accent-foreground
          focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
          ${className}
        `}
        aria-label={`Switch to ${isDark ? 'light' : 'dark'} theme`}
      >
        {isDark ? (
          <Sun className={iconSizeClasses[size]} />
        ) : (
          <Moon className={iconSizeClasses[size]} />
        )}
      </button>
    );
  }

  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      <Sun className={`${iconSizeClasses[size]} ${isDark ? 'text-muted-foreground' : 'text-amber-500'}`} />
      
      <Switch
        checked={isDark}
        onChange={toggleTheme}
        className={`
          ${isDark ? 'bg-primary' : 'bg-muted'}
          relative inline-flex ${sizeClasses[size]} shrink-0 cursor-pointer rounded-full border-2 border-transparent 
          transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
        `}
      >
        <span className="sr-only">Toggle theme</span>
        <span
          aria-hidden="true"
          className={`
            ${isDark ? 'translate-x-5' : 'translate-x-0'}
            pointer-events-none inline-block h-${size === 'sm' ? '5' : size === 'md' ? '6' : '7'} 
            w-${size === 'sm' ? '5' : size === 'md' ? '6' : '7'} transform rounded-full 
            bg-white shadow-lg ring-0 transition duration-200 ease-in-out
          `}
        />
      </Switch>
      
      <Moon className={`${iconSizeClasses[size]} ${isDark ? 'text-blue-400' : 'text-muted-foreground'}`} />
    </div>
  );
}

// Compact version for headers/toolbars
export function ThemeToggleCompact({ className = '' }: { className?: string }) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      onClick={toggleTheme}
      className={`
        relative inline-flex h-8 w-8 items-center justify-center rounded-md
        text-muted-foreground hover:text-foreground hover:bg-muted
        transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
        ${className}
      `}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} theme`}
    >
      {isDark ? (
        <Sun className="h-4 w-4" />
      ) : (
        <Moon className="h-4 w-4" />
      )}
    </button>
  );
}